// ─── components/Header/SiteHeader.jsx ─────────────────────────────────────
// The site header, in two decks (option A, chosen 2026-10-02): the sticky
// bar holds the logo, a wide search in the middle and your own controls; a
// thin second row under it lists the site's sections and scrolls away with
// the page. One row had grown to thirteen controls, which squeezed search to
// ~120px on a laptop.
//
// At rest the bar sits flat on a white band across the page. Once the page
// scrolls past a 24px sentinel it lifts into a detached, frosted pill: the
// band fades out and the pill's own plate fades in, so nothing changes size
// (only opacity/transform animate). Blur lives on this sticky element only.
// From lg, once row two has scrolled up under the bar, its links dock into
// the pill in the search's place and search folds to an icon (option B,
// 2026-10-05); focusing search brings it back.
//
// Below lg the page links move into a full-screen sheet behind a menu button
// that morphs into an X; links rise in one after another. The dashboard
// switcher and the avatar are one "me" chip: a colored 8px dot + the side
// you're on, then your avatar. Its menu holds the switch itself.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { PiBellLight, PiEnvelopeSimpleLight, PiCaretDownLight, PiArrowUpRightLight, PiArrowRightLight, PiShoppingBagLight, PiBriefcaseLight, PiBookOpenLight, PiPencilSimpleLight, PiTargetLight, PiPackageLight, PiCalendarBlankLight, PiHeartLight, PiGearLight, PiQuestionLight, PiSignOutLight, PiUserLight, PiCurrencyDollarLight, PiCheckLight, PiBell, PiChatText, PiCurrencyDollar, PiFileText, PiMagnifyingGlass, PiMagnifyingGlassLight, PiMedal, PiStar, PiVideoCamera, PiWarning } from 'react-icons/pi';

import SiteSearch from '../Search/SiteSearch';
import PhoneTabBar from './PhoneTabBar';
import Avatar from '../UI/Avatar';
import {
  Menu, MenuTrigger, MenuContent, MenuGroup, MenuItem, MenuLabel, MenuSeparator,
} from '../UI/Menu';
import useModalA11y from '../../hooks/useModalA11y';
import { useAuth } from '../../context/AuthContext';
import { useAuthModal } from '../../context/AuthModalContext';
import { useSocket } from '../../context/SocketContext';
import { useNotifications } from '../../context/NotificationContext';
import { hasAdminAccess, hasInstructorAccess, hasFacultyAccess } from '../../utils/roles';
import { checklistFor } from '../../utils/profileCompleteness';
import { formatUSD } from '../../utils/money';
import { NEEDS_YOU_ACTION, needsYou, groupByDay, shortTime } from '../../constants/notificationActions';
import unicornLogo from '../../assets/unicorn-logo.png';
import { BadgeDot, BadgeCount, BadgeTally, RAIL_CAP } from '../UI/Badge';
import { disablePushOnThisDevice } from '../../utils/push';

// Routes whose phone layout is a full-screen wizard: no site header or footer.
export const PHONE_WIZARD_ROUTES = /^\/(get-started|find-work\/(create|edit)-listing|job-postings\/(create|edit\/)|seller-onboarding|client-onboarding|signup$|academy\/create\/(?!project-studio)[^/]+$|academy\/tutorials\/[^/]+\/edit$|academy\/courses\/[^/]+\/learn$)/;

const NOTIF_CATEGORY_ICON = {
  messages: PiChatText,
  grades: PiMedal,
  reviews: PiStar,
  contracts: PiFileText,
  payouts: PiCurrencyDollar,
  webinars: PiVideoCamera,
  matches: PiMagnifyingGlass,
  system: PiBell,
  warning: PiWarning,
};

function NotifIcon({ category }) {
  const Icon = NOTIF_CATEGORY_ICON[category] || PiBell;
  return <Icon size={14} aria-hidden="true" />;
}
NotifIcon.propTypes = { category: PropTypes.string };

// One hue per dashboard destination (tailwind `mode.*`). Only ever an 8px
// identity dot now (the me chip, the dashboard rows), never a button fill.
const DASHBOARD_COLORS = {
  instructor: '#4F46E5',
  selling: '#E11D48',
  hiring: '#0F766E',
  mentor: '#7C3AED',
  admin: '#475569',
  faculty: '#B45309',
  team: '#0369A1',
};
const DASHBOARD_SHORT = {
  instructor: 'Teaching', selling: 'Selling', hiring: 'Hiring', mentor: 'Mentoring', admin: 'Admin', faculty: 'Faculty',
};

// Row two's page links: plain text, the current page underlined in ink. The
// row is white like the band above it, so a chip per link would only repeat
// the pill the old one-row header needed to keep eight links apart. Every
// link is bold (one weight, so nothing shifts width between routes); the
// current page is marked by ink colour and the underline, not weight.
const deckLink = (active) => `relative inline-flex h-11 items-center gap-1.5 whitespace-nowrap font-sans text-[14px] font-bold no-underline transition-colors duration-300 ease-spring ${
  active
    ? 'text-dark-primary after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-full after:bg-dark-primary'
    : 'text-ink-soft hover:text-dark-primary'
}`;
const iconBtn = 'relative grid h-10 w-10 flex-none place-items-center rounded-full text-ink-soft no-underline transition-colors duration-300 ease-spring hover:bg-dark-primary/[0.06] hover:text-dark-primary';
// Positioning only. The badge itself is <BadgeCount onArt ringClass="ring-surface" />,
// so its height, colour, weight and cap match every other count in the app
// instead of being the fourth size in a set of four.
const countBadge = 'absolute right-0 top-0.5';
// A nav count is a tally, not a summons: it says how many are in there, and
// the red is reserved for the pin on the icon that actually needs you.
const navCount = 'inline-grid h-[18px] min-w-[18px] place-items-center rounded-full bg-press-paper-2 px-1.5 text-[12px] font-extrabold tabular-nums text-press-muted';

export default function SiteHeader({ user }) {
  const location = useLocation();
  // Work / My Work / account menus are Radix DropdownMenus (components/UI/
  // Menu.jsx), controlled here so a route change can close them and opening
  // one can refresh the counts it shows.
  const [workOpen, setWorkOpen] = useState(false);
  const [myWorkOpen, setMyWorkOpen] = useState(false);
  const [academyOpen, setAcademyOpen] = useState(false);
  const [communityOpen, setCommunityOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [dashMenuOpen, setDashMenuOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  // Remembers whichever of the dashboards an instructor last actually
  // visited, so the me chip doesn't silently relabel itself "Teaching" the
  // moment they browse to an unrelated page. Persisted across refreshes.
  const [lastDashboardKey, setLastDashboardKey] = useState(() => {
    try { return localStorage.getItem('uf:lastDashboard') || 'instructor'; }
    catch { return 'instructor'; }
  });
  useEffect(() => {
    const key = location.pathname.startsWith('/seller/dashboard') ? 'selling'
      : location.pathname.startsWith('/hiring/dashboard') ? 'hiring'
      : location.pathname.startsWith('/mentor/dashboard') ? 'mentor'
      : location.pathname.startsWith('/admin') ? 'admin'
      : location.pathname.startsWith('/professor') ? 'faculty'
      : location.pathname.startsWith('/instructor/dashboard') ? 'instructor'
      : null;
    if (!key) return;
    setLastDashboardKey(key);
    try { localStorage.setItem('uf:lastDashboard', key); } catch { /* private mode etc — display-only default, fine to skip */ }
  }, [location.pathname]);
  const { setUser, refreshUser } = useAuth();
  const { openAuthModal } = useAuthModal();
  const navigate = useNavigate();

  // Badge counts + held earnings for the header. Fetched once per sign-in so
  // the My Work badge and the other-side dot are right without opening
  // anything, then refreshed whenever a menu that shows them opens. Not a
  // poll: a few seconds of staleness between opens is fine.
  const [menuStats, setMenuStats] = useState(null);
  const menusOpen = avatarOpen || myWorkOpen || sheetOpen;
  useEffect(() => {
    if (!user?._id) { setMenuStats(null); return; }
    fetch('/api/users/menu-stats', { credentials: 'include' })
      .then(res => (res.ok ? res.json() : null))
      .then(data => { if (data) setMenuStats(data); })
      .catch(() => {});
  }, [user?._id, menusOpen]);

  // Role/capability flags (instructor, admin, mentor) are set at login and
  // only change when someone ELSE's action touches the account, so they're
  // re-checked right as the account menu opens instead of polling. Keyed only
  // on `avatarOpen` — depending on `user` would loop on every refresh.
  useEffect(() => {
    if (!avatarOpen) return;
    refreshUser();
  }, [avatarOpen, refreshUser]);

  // Seller checklist (profile, Stripe, first listing) still open: a dot on
  // the me chip from anywhere, and a line in its menu.
  const [sellerStatus, setSellerStatus] = useState(null);
  useEffect(() => {
    if (user?.defaultView !== 'freelancing') { setSellerStatus(null); return undefined; }
    let cancelled = false;
    fetch('/api/users/seller-status', { credentials: 'include' })
      .then(res => (res.ok ? res.json() : null))
      .then(data => { if (!cancelled) setSellerStatus(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.defaultView, user?._id]);
  const sellerSetupIncomplete = !!sellerStatus && sellerStatus.strength.done < sellerStatus.strength.total;

  // Grace-period escalation: a quiet dot on the me chip from day one.
  // EmailVerifyReminder takes over with a real bar once the grace period ends.
  const emailUnverified = !!user && user.emailVerified === false;

  // Flip between selling and hiring. Saves defaultView (it decides which
  // side the menus lead with). Only moves you when `go` is set, or when
  // you're already on a side dashboard (then you land on the other one), so
  // switching from the nav never pulls you off the page you're reading.
  async function setSide(nextView, { go = false } = {}) {
    if (!user) return;
    const onSideDashboard = location.pathname.startsWith('/seller/dashboard') || location.pathname.startsWith('/hiring/dashboard');
    if (go || onSideDashboard) navigate(nextView === 'freelancing' ? '/seller/dashboard' : '/hiring/dashboard');
    if (user.defaultView === nextView) return;
    setUser(prev => ({ ...prev, defaultView: nextView }));
    try {
      await fetch('/api/users/default-view', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ defaultView: nextView }),
      });
    } catch {
      // Best-effort persistence — the optimistic UI update already applied.
    }
  }
  const switchToMode = (nextView) => setSide(nextView, { go: true });

  // ── Unified notification bell ───────────────────────────────────────────────
  const { unreadCount: notifUnread, items: notifList, markRead: markNotifRead, markAllRead: markAllNotifsRead } = useNotifications();
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = React.useRef(null);

  const closeAll = useCallback(() => {
    setWorkOpen(false);
    setMyWorkOpen(false);
    setAcademyOpen(false);
    setCommunityOpen(false);
    setAvatarOpen(false);
    setDashMenuOpen(false);
    setNotifOpen(false);
    setSheetOpen(false);
  }, []);

  useEffect(() => { closeAll(); }, [location.pathname, closeAll]);

  // Escape closes the bell's panel. (Radix handles its own menus; the sheet
  // handles its own via useModalA11y.)
  useEffect(() => {
    if (!notifOpen) return undefined;
    const handler = (e) => { if (e.key === 'Escape') setNotifOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [notifOpen]);

  // The Inbox badge counts unread messages from the source of truth
  // (GET /api/inbox/stats), separate from the bell's notification feed.
  const [inboxUnreadMessages, setInboxUnreadMessages] = useState(0);
  const { socket } = useSocket();

  const refreshInboxUnread = useCallback(() => {
    if (!user) { setInboxUnreadMessages(0); return; }
    fetch('/api/inbox/stats', { credentials: 'include' })
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (!data) return;
        setInboxUnreadMessages(data.unreadMessages || 0);
      })
      .catch(() => {});
  }, [user]);

  // Re-check on navigation as well as on arrival: opening a thread marks it
  // read server-side, so the badge has to clear when you leave the inbox.
  useEffect(() => { refreshInboxUnread(); }, [refreshInboxUnread, location.pathname]);

  // The inbox fires this the moment a thread is read, so the badge drops while
  // you're still on the page.
  useEffect(() => {
    window.addEventListener('uf:inbox-read', refreshInboxUnread);
    return () => window.removeEventListener('uf:inbox-read', refreshInboxUnread);
  }, [refreshInboxUnread]);

  // Live: a message arriving while you're on another page raises the badge.
  useEffect(() => {
    if (!socket) return undefined;
    const onNew = () => refreshInboxUnread();
    socket.on('newMessage', onNew);
    return () => socket.off('newMessage', onNew);
  }, [socket, refreshInboxUnread]);

  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  const openNotifPanel = () => {
    setNotifOpen(v => !v);
    setAvatarOpen(false);
    setWorkOpen(false);
  };

  // Signup/Login/Username have their own minimal chrome, so the form isn't
  // competing with the full site nav for vertical space.
  const hidden = location.pathname === '/signup' || location.pathname === '/login' || location.pathname === '/username';
  // Phone wizards (components/Shared/PhoneWizard.jsx) carry their own top bar.
  const phoneWizard = PHONE_WIZARD_ROUTES.test(location.pathname);

  // ── Condense on scroll ────────────────────────────────────────────────────
  // A 24px sentinel pinned to the top of the document; once it leaves the
  // viewport the bar lifts into the floating pill. IntersectionObserver, not
  // a scroll listener, so nothing runs per scroll frame.
  const sentinelRef = useRef(null);
  const [condensed, setCondensed] = useState(false);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([entry]) => setCondensed(!entry.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, [hidden]);

  // ── Section links dock into the bar (lg+) ────────────────────────────────
  // Once row two has slid up under the bar, its links take the search's
  // place in the pill and search folds to an icon (option B, 2026-10-05).
  // Keyed to row two itself rather than the 24px sentinel, so the links
  // never show twice: they arrive in the bar as row two leaves from under it.
  // Focusing the search (the icon, ⌘K, "/", or Tab) brings it back until
  // focus leaves it.
  const rowTwoRef = useRef(null);
  const midRef = useRef(null);
  const [rowTwoGone, setRowTwoGone] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const el = rowTwoRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setRowTwoGone(!entry.isIntersecting && entry.boundingClientRect.top < 72),
      { rootMargin: '-72px 0px 0px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hidden]);
  useEffect(() => { if (!rowTwoGone) setSearchOpen(false); }, [rowTwoGone]);
  const navDocked = rowTwoGone && !searchOpen;
  const openDockedSearch = () => midRef.current?.querySelector('input')?.focus();
  // Wait out SiteSearch's own 150ms blur so a click on a result still lands.
  const onSearchBlur = () => setTimeout(() => {
    if (!midRef.current?.contains(document.activeElement)) setSearchOpen(false);
  }, 200);

  // ── Phone sheet ───────────────────────────────────────────────────────────
  // Focus trap + Escape via useModalA11y, and the page underneath stops
  // scrolling while it's open.
  const sheetRef = useRef(null);
  useModalA11y(sheetRef, () => setSheetOpen(false), sheetOpen);
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [sheetOpen]);
  // Crossing into desktop width with the sheet open leaves it stranded
  // (it's lg:hidden) with the page still scroll-locked.
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => { if (e.matches) setSheetOpen(false); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [sheetOpen]);

  if (hidden) return null;

  const isWorkActive = ['/find-work', '/job-postings', '/browse-freelancers', '/hire-talent', '/my-listings'].some(p => location.pathname.startsWith(p));
  const isAcademyActive = location.pathname === '/academy' || location.pathname.startsWith('/academy/');
  const isHomeRoute = location.pathname === '/' || location.pathname === '/home';
  // Project Studio is its own standalone page (not under /academy/), but
  // conceptually it's still the second half of the Academy menu.
  const isProjectStudioActive = location.pathname.startsWith('/project-studio');

  // ── Mode-ordered menus ──────────────────────────────────────────────────────
  // `defaultView` decides ordering and emphasis only, never visibility.
  // Members hold both roles on one account, so hiding the other side would
  // strand someone who came to post a job and then wanted to sell a service.

  const WORK_FREELANCING = {
    key: 'freelancing',
    label: 'For freelancers',
    items: [
      { to: '/job-postings', label: 'Browse jobs', sub: 'Find jobs from clients looking for help' },
      { to: '/find-work', label: 'Post a service', sub: 'Offer your skills and products' },
    ],
  };
  const WORK_HIRING = {
    key: 'hiring',
    label: 'For clients',
    items: [
      { to: '/hire-talent', label: 'Browse services', sub: 'See what is offered' },
      { to: '/browse-freelancers', label: 'Browse freelancers', sub: 'UniFreelancer talent' },
      { to: '/post-a-job', label: 'Post a job', sub: 'Have talent apply to you' },
    ],
  };
  const workColumns = user?.defaultView === 'hiring'
    ? [WORK_HIRING, WORK_FREELANCING]
    : [WORK_FREELANCING, WORK_HIRING];

  const AVATAR_FREELANCING = [
    { to: '/my-listings', label: 'My services' },
    { to: '/find-work', label: 'Post a service' },
  ];
  const AVATAR_HIRING = [
    { to: '/post-a-job', label: 'Post a job' },
    { to: '/my-job-postings', label: 'My job postings', badge: menuStats?.activeJobPostings || 0 },
  ];
  // ── My Work ───────────────────────────────────────────────────────────────
  // Scoped to the side you're on (defaultView). Someone who does both gets
  // the other side as one line at the foot of the menu whenever it has
  // something in progress. Teaching and Admin are roles, not sides, so they
  // are added regardless.
  const isHiring = user?.defaultView === 'hiring';
  const buyingActive = (menuStats?.ordersBuying || 0) + (menuStats?.contractsBuying || 0);
  const sellingActive = (menuStats?.ordersSelling || 0) + (menuStats?.contractsSelling || 0);
  const sideActive = isHiring ? buyingActive : sellingActive;
  const otherSideActive = isHiring ? sellingActive : buyingActive;
  const inProgress = (n, fallback) => (n > 0 ? `${n} in progress` : fallback);

  const MY_WORK_LINKS = [
    ...(isHiring
      ? [
        { to: '/orders', label: 'Orders & contracts', sub: inProgress(buyingActive, 'Services you bought and people you hired'), badge: buyingActive },
        // Job postings earns its row with the first posting; until then the
        // menu offers posting a job as the second way to hire instead.
        menuStats?.hasJobPostings
          ? { to: '/hiring/dashboard?section=postings', label: 'Job postings', sub: menuStats.activeJobPostings ? `${menuStats.activeJobPostings} open` : 'Applicants and hires', badge: 0 }
          : { to: '/post-a-job', label: 'Post a job', sub: 'Need something custom? Students apply to you', badge: 0 },
      ]
      : [
        { to: '/orders', label: 'Orders & contracts', sub: inProgress(sellingActive, 'Work you’re delivering'), badge: sellingActive },
        { to: '/my-listings', label: 'My services', sub: 'The services you offer', badge: 0 },
        { to: '/my-proposals', label: 'Proposals', sub: 'Job applications you’ve sent', badge: 0 },
      ]),
    ...(hasInstructorAccess(user)
      ? [{ to: '/instructor/dashboard', label: 'Teaching', sub: 'Courses and submissions', badge: menuStats?.instructorPendingCount || 0 }]
      : []),
    ...(hasAdminAccess(user)
      ? [{ to: '/admin', label: 'Admin', sub: 'Platform management', badge: menuStats?.adminPendingCount || 0 }]
      : []),
  ];
  // Only orders and contracts can actually be waiting on you; live services
  // and open postings are states, not tasks.
  const myWorkNeedsYou = sideActive;
  const isMyWorkActive = ['/orders', '/my-listings', '/my-proposals', '/my-job-postings', '/work/']
    .some((to) => location.pathname.startsWith(to));

  const FREELANCING_MODE = { key: 'freelancing', label: 'Freelancing', links: AVATAR_FREELANCING };
  const HIRING_MODE = { key: 'hiring', label: 'Hiring', links: AVATAR_HIRING };
  const activeMode = user?.defaultView === 'hiring' ? HIRING_MODE : FREELANCING_MODE;
  const otherMode = activeMode.key === 'hiring' ? FREELANCING_MODE : HIRING_MODE;
  // Only for accounts that actually sell: a service, a job application, or
  // work underway as the freelancer. Having a freelancerProfile subdoc alone
  // means nothing — every account gets one — so buyers were being nagged.
  const sellsSomething = !!(menuStats && (menuStats.hasListings || menuStats.hasApplied
    || menuStats.ordersSelling || menuStats.contractsSelling));
  const needsPayoutSetup = sellsSomething && user?.freelancerProfile?.stripeOnboardingComplete === false;

  // The *other* side only earns a whole group once there's a reason to
  // believe it applies. Before menuStats has loaded, show it rather than
  // flashing it away and back.
  const otherModeHasActivity = !menuStats ? true : (
    otherMode.key === 'hiring'
      ? !!(menuStats.hasJobPostings || menuStats.ordersBuying || menuStats.contractsBuying)
      : !!(menuStats.hasListings || menuStats.ordersSelling || menuStats.contractsSelling)
  );

  // Learning & Teaching: instructors and mentors always; otherwise only once
  // someone has enrolled in something (and before menuStats loads).
  const showLearningSection = hasInstructorAccess(user) || user?.isMentorSomewhere
    || !menuStats || (menuStats.enrolledCourseCount || 0) > 0;

  // ── The me chip ───────────────────────────────────────────────────────────
  // Which dashboard you're on (or last used): the dot's color and the label.
  const isInstructor = hasInstructorAccess(user);
  const isMember = user?.accountType === 'member';
  const activeKey = !user ? null
    : isInstructor ? (
      location.pathname.startsWith('/seller/dashboard') ? 'selling'
        : location.pathname.startsWith('/hiring/dashboard') ? 'hiring'
        : location.pathname.startsWith('/mentor/dashboard') ? 'mentor'
        : location.pathname.startsWith('/admin') ? 'admin'
        : location.pathname.startsWith('/professor') ? 'faculty'
        : location.pathname.startsWith('/instructor/dashboard') ? 'instructor'
        : lastDashboardKey
    )
    : isMember ? (location.pathname.startsWith('/admin') ? 'admin' : user.defaultView === 'freelancing' ? 'selling' : 'hiring')
    : null;
  // Every dashboard this account can open: the nav's dashboard control
  // (instructors) and the account menu's Dashboards group list these.
  // Selling/Hiring rows also switch defaultView (via `side`).
  const dashboardRows = !user ? [] : [
    ...(isInstructor ? [{ key: 'instructor', label: 'Instructor dashboard', to: '/instructor/dashboard' }] : []),
    ...(isMember || isInstructor ? [
      { key: 'selling', label: 'Selling dashboard', to: '/seller/dashboard', side: 'freelancing' },
      { key: 'hiring', label: 'Hiring dashboard', to: '/hiring/dashboard', side: 'hiring' },
    ] : []),
    ...(user.isMentorSomewhere ? [{ key: 'mentor', label: 'Mentor dashboard', to: '/mentor/dashboard' }] : []),
    ...(hasAdminAccess(user) ? [{ key: 'admin', label: 'Admin dashboard', to: '/admin' }] : []),
    ...(hasFacultyAccess(user) ? [{ key: 'faculty', label: 'Faculty dashboard', to: '/professor' }] : []),
  ];
  const activeDashboard = dashboardRows.find(d => d.key === activeKey) || dashboardRows[0] || null;
  // One row per team this account is on, after the role dashboards. Kept out of
  // dashboardRows on purpose: that list decides which dashboard the me chip
  // names, and a team is something you also belong to, not a mode you work in.
  const teamRows = (menuStats?.teams || []).map(t => ({
    key: `team-${t.slug}`, label: `${t.name} (team)`, to: `/teams/${t.slug}/dashboard`, needs: t.needsYou || 0,
  }));
  // What is waiting on you inside each dashboard. Admin and Teaching already
  // had one; Hiring did not, which is why the dashboard you most often have a
  // decision sitting in was the one the menu said nothing about.
  const dashboardNeeds = {
    admin:      menuStats?.adminPendingCount || 0,
    instructor: menuStats?.instructorPendingCount || 0,
    hiring:     menuStats?.hiringNeedsYou || 0,
  };
  // Anything needing you on a dashboard you are NOT currently looking at —
  // the caret is the only way to reach those, so it carries the dot.
  // The team dashboard you are standing in, if any. The pill names it "Team"
  // rather than keeping the label of whichever dashboard you used last.
  const onTeam = teamRows.find(t => location.pathname.startsWith(t.to)) || null;
  const needsElsewhere = dashboardRows.some(d => (onTeam || d.key !== activeKey) && (dashboardNeeds[d.key] || 0) > 0)
    || teamRows.some(t => t.needs > 0 && !location.pathname.startsWith(t.to));
  const dashRowItems = dashboardRows.map(d => (
    <MenuItem
      key={d.key}
      to={d.side ? undefined : d.to}
      onSelect={d.side ? () => switchToMode(d.side) : undefined}
      icon={<span className="grid h-[18px] w-[18px] place-items-center"><span className="h-2 w-2 rounded-full" style={{ background: DASHBOARD_COLORS[d.key] }} /></span>}
    >
      <span className={`flex items-center justify-between gap-2 ${activeKey === d.key ? 'font-semibold' : ''}`}>
        {d.label}
        {/* Red, not the menu's grey tally: this is the row the caret's red
            dot is pointing at, so it has to read as the same signal. */}
        <span className="flex items-center gap-1.5">
          <BadgeCount value={dashboardNeeds[d.key] || 0} label={`${dashboardNeeds[d.key] || 0} waiting on you`} />
          {activeKey === d.key && <PiCheckLight size={16} aria-label="Current" />}
        </span>
      </span>
    </MenuItem>
  ));
  const teamRowItems = teamRows.map(t => {
    const here = location.pathname.startsWith(t.to);
    return (
      <MenuItem
        key={t.key}
        to={t.to}
        icon={<span className="grid h-[18px] w-[18px] place-items-center"><span className="h-2 w-2 rounded-full" style={{ background: DASHBOARD_COLORS.team }} /></span>}
      >
        <span className={`flex items-center justify-between gap-2 ${here ? 'font-semibold' : ''}`}>
          <span className="min-w-0 truncate">{t.label}</span>
          <span className="flex items-center gap-1.5">
            <BadgeCount value={t.needs} label={`${t.needs} waiting on you`} />
            {here && <PiCheckLight size={16} aria-label="Current" />}
          </span>
        </span>
      </MenuItem>
    );
  });
  const chipAttention = sellerSetupIncomplete || emailUnverified;

  // Visible notifications: the setup nudge only while setup is actually open.
  const visibleNotifs = notifList.filter(n => n.type !== 'onboarding-nudge' || !checklistFor(user).canProceed).slice(0, 12);

  const signOut = async () => {
    closeAll();
    await disablePushOnThisDevice();
    await fetch('/api/users/logout', { method: 'POST', credentials: 'include' });
    setUser(null);
    window.location.href = '/';
  };

  // Sheet link list: the page links plus the most-used destinations from
  // the Work menu, so a phone never has to open a second menu.
  const sheetLinks = [
    { to: '/home', label: 'Home', active: location.pathname === '/' || location.pathname === '/home' },
    { to: '/hire-talent', label: 'Browse services', active: location.pathname.startsWith('/hire-talent') },
    { to: '/job-postings', label: 'Browse jobs', active: location.pathname.startsWith('/job-postings') },
    ...(user && activeDashboard ? [
      { to: activeDashboard.to, label: 'Dashboard', active: location.pathname === activeDashboard.to },
    ] : []),
    // Inbox isn't listed: its icon sits in the bar at every width.
    ...(user ? [
      { to: '/orders', label: 'My Work', badge: myWorkNeedsYou, active: isMyWorkActive },
    ] : []),
    { to: '/academy', label: 'Academy', active: isAcademyActive },
  ];
  // Community (Social, Teams, Project Studio) sits in the quieter row below,
  // as it sits in one menu on desktop, until those spaces are busier.
  const sheetMore = [
    { to: '/social', label: 'Social' },
    { to: '/teams', label: 'Teams' },
    ...teamRows.map(t => ({ to: t.to, label: t.label })),
    { to: '/project-studio', label: 'Project Studio' },
    { to: '/find-work', label: 'Post a service' },
    { to: '/post-a-job', label: 'Post a job' },
    { to: '/browse-freelancers', label: 'Freelancers' },
    ...(user ? [{ to: '/saved', label: 'Saved' }, { to: '/calendar', label: 'Calendar' }, { to: '/profile', label: 'Profile & settings' }] : []),
    { to: '/about', label: 'About' },
    { to: '/help', label: 'Help' },
  ];

  // The section links, rendered twice: in row two under the bar, and docked
  // inside the bar once row two has scrolled away. Only the visible copy may
  // hold a menu open, so one open state never pops two panels.
  const isCommunityActive = location.pathname.startsWith('/teams') || location.pathname.startsWith('/social');
  const renderSectionLinks = (where) => {
    const live = (where === 'bar') === navDocked;
    // Docked links rise into the pill one after another, as if lifted up
    // from row two, and leave together, faster. Reduced motion keeps the
    // fade and drops the travel.
    const dock = (i, node) => (where === 'bar' ? (
      <span
        key={i}
        className={`inline-flex transition-[opacity,transform] ease-spring motion-reduce:transform-none ${navDocked ? 'translate-y-0 opacity-100 duration-500' : 'translate-y-2 opacity-0 duration-200'}`}
        style={{ transitionDelay: navDocked ? `${60 + i * 35}ms` : '0ms' }}
      >
        {node}
      </span>
    ) : node);
    return (
      <>
        {dock(0, <Link to="/home" className={deckLink(isHomeRoute)} aria-current={location.pathname === '/home' ? 'page' : undefined}>Home</Link>)}

        {dock(1, <Menu open={workOpen && live} onOpenChange={setWorkOpen} modal={false}>
          <MenuTrigger asChild>
            <button type="button" className={deckLink(isWorkActive || workOpen)}>
              Work
              <PiCaretDownLight size={13} aria-hidden="true" className={`transition-transform duration-300 ease-spring ${workOpen ? 'rotate-180' : ''}`} />
            </button>
          </MenuTrigger>
          <MenuContent align="start" className="w-[min(500px,calc(100vw-24px))]" coreClassName="grid gap-1 min-[420px]:grid-cols-2">
            {/* Both sides are always here; your default side leads and
                sits on the tinted panel. */}
            {workColumns.map((col, i) => {
              const isYours = i === 0 && !!user;
              return (
                <MenuGroup key={col.key} className={`rounded-[14px] p-1 ${isYours ? 'bg-press-paper/70' : ''}`}>
                  <MenuLabel>{col.label}{isYours ? ' · you' : ''}</MenuLabel>
                  {col.items.map(item => (
                    <MenuItem key={item.to} to={item.to} className="group items-start">
                      <span className="block font-semibold">{item.label}</span>
                      <span className="block text-xs font-normal text-press-muted">{item.sub}</span>
                    </MenuItem>
                  ))}
                </MenuGroup>
              );
            })}
          </MenuContent>
        </Menu>)}

        {user && dock(2, (
          <Menu open={myWorkOpen && live} onOpenChange={setMyWorkOpen} modal={false}>
            <MenuTrigger asChild>
              <button type="button" className={deckLink(isMyWorkActive || myWorkOpen)}>
                My Work
                {myWorkNeedsYou > 0 && (
                  <span aria-label={`${myWorkNeedsYou} item${myWorkNeedsYou === 1 ? '' : 's'} need your attention`} className={navCount}>
                    {myWorkNeedsYou > 9 ? '9+' : myWorkNeedsYou}
                  </span>
                )}
                <PiCaretDownLight size={13} aria-hidden="true" className={`transition-transform duration-300 ease-spring ${myWorkOpen ? 'rotate-180' : ''}`} />
              </button>
            </MenuTrigger>
            <MenuContent align="start" className="w-[min(300px,calc(100vw-24px))]">
              {MY_WORK_LINKS.map(({ to, label, sub, badge }) => (
                <MenuItem key={to} to={to} badge={badge}>
                  <span className="block font-semibold">{label}</span>
                  <span className="block text-xs font-normal text-press-muted">{sub}</span>
                </MenuItem>
              ))}
              {/* The other side, for members who work both: one line
                  whenever it has something in progress, so they never
                  have to switch just to check. */}
              {otherSideActive > 0 && (
                <>
                  <MenuSeparator />
                  <MenuItem onSelect={() => switchToMode(isHiring ? 'freelancing' : 'hiring')}>
                    <span className="block font-semibold">Your {isHiring ? 'selling' : 'hiring'} side</span>
                    <span className="block text-xs font-normal text-press-muted">{otherSideActive} in progress · switch</span>
                  </MenuItem>
                </>
              )}
            </MenuContent>
          </Menu>
        ))}

        {dock(3, <Menu open={academyOpen && live} onOpenChange={setAcademyOpen} modal={false}>
          <MenuTrigger asChild>
            <button type="button" className={deckLink(isAcademyActive || isProjectStudioActive || academyOpen)}>
              Academy
              <PiCaretDownLight size={13} aria-hidden="true" className={`transition-transform duration-300 ease-spring ${academyOpen ? 'rotate-180' : ''}`} />
            </button>
          </MenuTrigger>
          <MenuContent align="start" className="w-[min(260px,calc(100vw-24px))]">
            <MenuItem to="/academy">
              <span className="block font-semibold">Academy</span>
              <span className="block text-xs font-normal text-press-muted">Courses, webinars, tutorials and podcasts</span>
            </MenuItem>
            <MenuItem to="/project-studio">
              <span className="block font-semibold">Project Studio</span>
              <span className="block text-xs font-normal text-press-muted">Real client projects for your university</span>
            </MenuItem>
          </MenuContent>
        </Menu>)}
        {dock(4, <Menu open={communityOpen && live} onOpenChange={setCommunityOpen} modal={false}>
          <MenuTrigger asChild>
            <button type="button" className={deckLink(isCommunityActive || communityOpen)}>
              Community
              <PiCaretDownLight size={13} aria-hidden="true" className={`transition-transform duration-300 ease-spring ${communityOpen ? 'rotate-180' : ''}`} />
            </button>
          </MenuTrigger>
          <MenuContent align="start" className="w-[min(260px,calc(100vw-24px))]">
            <MenuItem to="/social">
              <span className="block font-semibold">Social</span>
              <span className="block text-xs font-normal text-press-muted">Your school&apos;s boards and course pages</span>
            </MenuItem>
            <MenuItem to="/teams">
              <span className="block font-semibold">Teams</span>
              <span className="block text-xs font-normal text-press-muted">Freelance together as a group</span>
            </MenuItem>
          </MenuContent>
        </Menu>)}
      </>
    );
  };

  return (
    <>
      {/* Top-of-document sentinel for the condense observer. */}
      <div ref={sentinelRef} aria-hidden="true" className="pointer-events-none absolute left-0 top-0 h-6 w-px" />

      <header className={`sticky top-0 z-50 px-2 py-2 sm:px-3 ${phoneWizard ? 'phone:hidden' : ''}`}>
        {/* At rest: a white band across the page. Condensed: the band fades
            and the pill's own frosted plate fades in. */}
        <div aria-hidden="true" className={`absolute inset-0 border-b border-line bg-surface transition-opacity duration-500 ease-spring motion-reduce:transition-none ${condensed ? 'opacity-0' : 'opacity-100'}`} />
        <div className="relative isolate mx-auto flex h-14 max-w-[1320px] items-center gap-2 rounded-full pl-3 pr-1.5 sm:pl-4 lg:gap-3">
          <div aria-hidden="true" className={`absolute inset-0 -z-10 rounded-full bg-surface/80 shadow-[0_0_0_1px_rgba(26,34,48,0.07),0_14px_34px_-16px_rgba(26,34,48,0.28)] backdrop-blur-xl backdrop-saturate-150 transition-[opacity,transform] duration-700 ease-spring motion-reduce:transition-none ${condensed ? 'scale-100 opacity-100' : 'scale-[0.98] opacity-0'}`} />

          <Link to="/" aria-label="UniFreelancer home" className="flex flex-none items-center gap-2.5 no-underline">
            <img src={unicornLogo} width={958} height={1563} alt="" aria-hidden="true" className="block h-9 w-auto" />
            {/* Docked links need the room below xl, so the wordmark tucks
                away behind the logo while they're up. */}
            <span className={`hidden overflow-hidden whitespace-nowrap font-brand text-[20px] font-extrabold leading-[1.2] tracking-[-0.03em] text-[#0f1117] transition-[max-width,opacity] duration-500 ease-spring motion-reduce:transition-none sm:inline-block ${navDocked ? 'max-w-[160px] [@media(min-width:1024px)_and_(max-width:1279px)]:max-w-0 [@media(min-width:1024px)_and_(max-width:1279px)]:opacity-0' : 'max-w-[160px]'}`} translate="no">UniFreelancer</span>
          </Link>


          {/* ── Middle of the bar, from md: search, or (lg+, once row two has
              scrolled away) the section links. The links come first in the
              DOM so Tab reaches them before the folded search. ── */}
          <div ref={midRef} className={`relative hidden min-w-0 flex-1 justify-center px-2 md:flex ${navDocked ? 'lg:px-2' : 'lg:px-6'}`}>
            <nav
              aria-label="Sections"
              inert={navDocked ? undefined : ''}
              className={`absolute inset-0 hidden items-center justify-center gap-5 xl:gap-7 lg:flex ${navDocked ? '' : 'pointer-events-none'}`}
            >
              {renderSectionLinks('bar')}
            </nav>
            <div
              onFocus={() => setSearchOpen(true)}
              onBlur={onSearchBlur}
              className={`flex w-full max-w-[560px] transition-[opacity,transform,filter] ease-spring motion-reduce:transform-none motion-reduce:filter-none ${navDocked ? 'duration-200 lg:pointer-events-none lg:scale-[0.97] lg:opacity-0 lg:blur-[2px]' : 'duration-500'}`}
            >
              <SiteSearch user={user} />
            </div>
          </div>

          {/* ── Right side ── */}
          <div className="ml-auto flex flex-none items-center justify-end gap-0.5 sm:gap-1 md:ml-0">

            {/* ── Folded search (lg+, links docked) ── */}
            <button
              type="button"
              onClick={openDockedSearch}
              aria-label="Search"
              tabIndex={navDocked ? 0 : -1}
              aria-hidden={navDocked ? undefined : true}
              className={`relative hidden h-10 flex-none place-items-center overflow-hidden rounded-full text-dark-primary transition-[width,opacity,transform,background-color] duration-500 ease-spring hover:bg-dark-primary/[0.06] motion-reduce:transition-none lg:grid ${navDocked ? 'w-10 scale-100 opacity-100' : 'pointer-events-none w-0 scale-75 opacity-0'}`}
            >
              <PiMagnifyingGlassLight size={21} aria-hidden="true" />
            </button>

            {/* ── Dashboards, one click away ──
                Members: Selling | Hiring. The other side switches you over
                right here; your current side opens its dashboard.
                Instructors (more than two dashboards): the current one as a
                link, and a caret listing the rest. */}
            {user && isMember && !isInstructor && (
              <div role="group" aria-label="Selling or hiring" className="mr-1 hidden flex-none items-center rounded-full bg-dark-primary/[0.045] p-1 ring-1 ring-inset ring-dark-primary/[0.05] sm:flex">
                {/* The dot answers "which dashboard needs me", so it is lit
                    per side on its own merits — including the side you are
                    already on, which used to be the one place it could never
                    appear (`!on && …`). It also no longer lights for merely
                    having active work: that is the normal state of an account
                    that uses the site, and a permanently-lit dot is one
                    nobody reads. Only a real decision lights it. */}
                {[
                  { view: 'freelancing', key: 'selling', label: 'Selling', to: '/seller/dashboard',
                    dot: sellerSetupIncomplete, why: 'Seller setup incomplete' },
                  { view: 'hiring', key: 'hiring', label: 'Hiring', to: '/hiring/dashboard',
                    dot: (menuStats?.hiringNeedsYou || 0) > 0,
                    why: `${menuStats?.hiringNeedsYou || 0} ${(menuStats?.hiringNeedsYou || 0) === 1 ? 'proposal is' : 'proposals are'} waiting on your decision` },
                ].map(side => {
                  const on = !onTeam && activeMode.key === side.view;
                  const showDot = side.dot;
                  return (
                    <button
                      key={side.view}
                      type="button"
                      aria-pressed={on}
                      title={side.dot
                        ? `${side.why}. Open your ${side.label.toLowerCase()} dashboard`
                        : `Open your ${side.label.toLowerCase()} dashboard`}
                      // Either side opens its dashboard in one click and makes
                      // it your side (2026-10-03). It used to only flip sides
                      // in place unless you were already on a dashboard, so
                      // reaching the other dashboard took two clicks.
                      onClick={() => switchToMode(side.view)}
                      className={`relative inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3 font-sans text-[13px] font-semibold transition-[background-color,color,box-shadow] duration-300 ease-spring motion-reduce:transition-none ${
                        on ? 'bg-dark-primary text-white shadow-[0_1px_2px_rgba(26,34,48,0.2)]' : 'text-ink-soft hover:text-dark-primary'
                      }`}
                    >
                      <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ background: DASHBOARD_COLORS[side.key] }} />
                      {side.label}
                      {on && <PiArrowUpRightLight size={12} aria-hidden="true" className="-mr-0.5 opacity-70" />}
                      {showDot && (
                        <span className="absolute -right-0.5 -top-0.5">
                          <BadgeDot onArt ringClass="ring-surface" />
                          <span className="sr-only">{side.why}</span>
                        </span>
                      )}
                    </button>
                  );
                })}
                {onTeam && (
                  <Link
                    to={onTeam.to}
                    aria-current="page"
                    title={`Open ${onTeam.label}`}
                    className="relative inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full bg-dark-primary px-3 font-sans text-[13px] font-semibold text-white no-underline shadow-[0_1px_2px_rgba(26,34,48,0.2)]"
                  >
                    <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ background: DASHBOARD_COLORS.team }} />
                    Team
                  </Link>
                )}
              </div>
            )}
            {user && isInstructor && activeDashboard && (
              <div className="mr-1 hidden flex-none items-center rounded-full bg-dark-primary font-sans text-white sm:flex">
                <Link
                  to={onTeam ? onTeam.to : activeDashboard.to}
                  className="inline-flex h-10 items-center gap-2 rounded-l-full pl-3.5 pr-2.5 text-[13px] font-semibold text-white no-underline"
                  title={onTeam ? `Open ${onTeam.label}` : `Open ${activeDashboard.label.toLowerCase()}`}
                >
                  <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ background: DASHBOARD_COLORS[onTeam ? 'team' : activeDashboard.key], boxShadow: `0 0 0 2px ${DASHBOARD_COLORS[onTeam ? 'team' : activeDashboard.key]}55` }} />
                  {onTeam ? 'Team' : DASHBOARD_SHORT[activeDashboard.key]}
                </Link>
                <Menu open={dashMenuOpen} onOpenChange={setDashMenuOpen} modal={false}>
                  <MenuTrigger asChild>
                    <button type="button" aria-label="Switch dashboard" className="relative grid h-10 w-9 place-items-center rounded-r-full border-l border-white/15 text-white/80 transition-colors hover:text-white">
                      <PiCaretDownLight size={13} aria-hidden="true" className={`transition-transform duration-300 ease-spring ${dashMenuOpen ? 'rotate-180' : ''}`} />
                      {needsElsewhere && (
                        <span className="absolute right-1 top-1.5">
                          <BadgeDot onArt ringClass="ring-dark-primary" />
                          <span className="sr-only">Another dashboard needs you</span>
                        </span>
                      )}
                    </button>
                  </MenuTrigger>
                  <MenuContent className="w-[min(270px,calc(100vw-24px))]">
                    <MenuLabel>Dashboards</MenuLabel>
                    {dashRowItems}
                    {teamRowItems}
                  </MenuContent>
                </Menu>
              </div>
            )}

            {user && (
              <div ref={notifRef} className="relative">
                <button
                  type="button"
                  onClick={openNotifPanel}
                  aria-label="Notifications"
                  aria-haspopup="true"
                  aria-expanded={notifOpen}
                  className={iconBtn}
                >
                  <PiBellLight size={20} aria-hidden="true" />
                  <BadgeCount
                    value={notifUnread}
                    max={RAIL_CAP}
                    label={`${notifUnread} unread notification${notifUnread === 1 ? '' : 's'}`}
                    onArt
                    ringClass="ring-surface"
                    className={countBadge}
                  />
                </button>

                {notifOpen && (
                  <NotificationsPopover
                    items={visibleNotifs}
                    unreadCount={notifUnread}
                    onMarkAllRead={markAllNotifsRead}
                    onClose={() => setNotifOpen(false)}
                    onOpen={(n, e) => {
                      setNotifOpen(false);
                      if (!n.read) markNotifRead(n._id);
                      // No page to link to for the setup nudge: it opens the
                      // setup flow directly instead of a dead /notifications.
                      if (n.type === 'onboarding-nudge') {
                        e.preventDefault();
                        navigate('/get-started', { state: { notificationId: n._id } });
                      }
                    }}
                  />
                )}
              </div>
            )}

            {user && (
              <Link to="/inbox" aria-label="Inbox" className={`${iconBtn} phone:hidden`}>
                <PiEnvelopeSimpleLight size={20} aria-hidden="true" />
                <BadgeCount
                  value={inboxUnreadMessages}
                  max={RAIL_CAP}
                  label={`${inboxUnreadMessages} unread message${inboxUnreadMessages === 1 ? '' : 's'}`}
                  onArt
                  ringClass="ring-surface"
                  className={countBadge}
                />
              </Link>
            )}

            {user ? (
              <Menu open={avatarOpen} onOpenChange={setAvatarOpen} modal={false}>
                <MenuTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setNotifOpen(false)}
                    aria-label="Account menu"
                    className="relative ml-0.5 flex h-11 w-11 flex-none items-center justify-center rounded-full bg-dark-primary/[0.06] p-[3px] transition-transform duration-300 ease-spring active:scale-[0.97] motion-reduce:transition-none"
                  >
                    <Avatar user={user} size={38} />
                    {chipAttention && (
                      <span
                        className="absolute -right-0.5 -top-0.5"
                      >
                        <BadgeDot onArt ringClass="ring-surface" />
                        <span className="sr-only">{emailUnverified ? 'Email not verified' : 'Seller setup incomplete'}</span>
                      </span>
                    )}
                  </button>
                </MenuTrigger>
                <MenuContent className="w-[min(320px,calc(100vw-24px))]">
                  {/* Who you are. */}
                  <div className="flex items-center gap-3 px-2.5 pb-2 pt-2">
                    <Avatar user={user} size={40} />
                    <div className="min-w-0">
                      <p className="m-0 truncate text-[14px] font-bold text-dark-primary">{user.firstName} {user.lastName}</p>
                      <p className="m-0 truncate text-xs text-ink-soft">{user.email}</p>
                    </div>
                  </div>
                  {/* The menu's main destination, so it reads as a button, not a row. */}
                  <MenuItem
                    to="/profile"
                    icon={<PiUserLight size={18} />}
                    className="mb-1 border border-line !font-bold data-[highlighted]:border-dark-primary"
                  >
                    View profile
                  </MenuItem>

                  {(dashboardRows.length > 0 || teamRows.length > 0) && (
                    <MenuGroup>
                      <MenuLabel>Dashboards</MenuLabel>
                      {dashRowItems}
                      {teamRowItems}
                    </MenuGroup>
                  )}

                  {!!menuStats && (
                    <div className="flex gap-1.5 px-1 pb-1 pt-2">
                      {!!menuStats.heldEarnings && (
                        <StatChip value={formatUSD(menuStats.heldEarnings, 2)} label="Escrow" />
                      )}
                      <StatChip value={menuStats.activeOrders || 0} label="Orders" />
                      <StatChip value={menuStats.activeContracts || 0} label="Contracts" />
                    </div>
                  )}

                  {sellerSetupIncomplete && (
                    <MenuItem to="/seller/dashboard" icon={<PiShoppingBagLight size={18} className="text-notify-deep" />} className="mt-1 bg-notify/[0.08] font-semibold text-notify-deep data-[highlighted]:bg-notify/[0.14]">
                      <span className="flex items-center justify-between">Finish setting up to sell <PiArrowRightLight size={15} aria-hidden="true" /></span>
                    </MenuItem>
                  )}
                  {needsPayoutSetup && (
                    <MenuItem to="/settings?stripe=nudge" icon={<PiCurrencyDollarLight size={18} className="text-accent-secondary" />} className="mt-1 bg-accent/[0.1] font-semibold text-accent-secondary data-[highlighted]:bg-accent/[0.16]">
                      <span className="flex items-center justify-between">Set up payouts to get paid <PiArrowRightLight size={15} aria-hidden="true" /></span>
                    </MenuItem>
                  )}

                  <MenuSeparator />
                  {/* Your default side always shows; the other side joins
                      once otherModeHasActivity says there's a reason to. */}
                  <MenuGroup>
                    <MenuLabel>{activeMode.key === 'freelancing' ? 'Selling' : 'Buying'}</MenuLabel>
                    {activeMode.links.map(link => (
                      <MenuItem key={link.to} to={link.to} badge={link.badge}
                        icon={activeMode.key === 'freelancing' ? <PiShoppingBagLight size={18} /> : <PiBriefcaseLight size={18} />}>
                        {link.label}
                      </MenuItem>
                    ))}
                  </MenuGroup>
                  {otherModeHasActivity && (
                    <MenuGroup>
                      <MenuLabel>{otherMode.label}</MenuLabel>
                      {otherMode.links.map(link => (
                        <MenuItem key={link.to} to={link.to} badge={link.badge}
                          icon={otherMode.key === 'freelancing' ? <PiShoppingBagLight size={18} /> : <PiBriefcaseLight size={18} />}>
                          {link.label}
                        </MenuItem>
                      ))}
                    </MenuGroup>
                  )}

                  {showLearningSection && (
                    <MenuGroup>
                      <MenuLabel>Learning and teaching</MenuLabel>
                      <MenuItem to="/academy/my-library" icon={<PiBookOpenLight size={18} />}>My learning</MenuItem>
                      <MenuItem to="/academy/create/course" icon={<PiPencilSimpleLight size={18} />}>
                        {isInstructor ? 'Create a course' : 'Teach on UniFreelancer'}
                      </MenuItem>
                      {isInstructor && (
                        <MenuItem to="/academy/project-studio/manage" icon={<PiTargetLight size={18} />}>Project Studio</MenuItem>
                      )}
                    </MenuGroup>
                  )}

                  <MenuGroup>
                    <MenuLabel>Workspace</MenuLabel>
                    <MenuItem to="/orders" badge={(menuStats?.activeOrders || 0) + (menuStats?.activeContracts || 0)} icon={<PiPackageLight size={18} />}>My Work</MenuItem>
                    <MenuItem to="/saved" icon={<PiHeartLight size={18} />}>Saved</MenuItem>
                    <MenuItem to="/calendar" icon={<PiCalendarBlankLight size={18} />}>Calendar</MenuItem>
                    <MenuItem to="/profile" icon={<PiGearLight size={18} />}>Profile and settings</MenuItem>
                  </MenuGroup>

                  <MenuSeparator />
                  <MenuItem to="/help" icon={<PiQuestionLight size={18} />}>Help and support</MenuItem>
                  <MenuItem onSelect={signOut} tone="quiet" icon={<PiSignOutLight size={18} />}>Sign out</MenuItem>
                </MenuContent>
              </Menu>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => openAuthModal({ mode: 'signin' })}
                  className="hidden h-10 whitespace-nowrap rounded-full px-3.5 font-sans text-[14px] font-medium text-ink-soft transition-colors duration-300 ease-spring hover:bg-dark-primary/[0.06] hover:text-dark-primary sm:inline-flex sm:items-center"
                >
                  Log in
                </button>
                <button
                  type="button"
                  onClick={() => openAuthModal({ mode: 'signup' })}
                  className="btn-island btn-island--sm btn-island--navy ml-1"
                >
                  Join
                  <span className="btn-island-ic"><PiArrowUpRightLight size={15} aria-hidden="true" /></span>
                </button>
              </>
            )}

            {/* ── Menu button (below lg): two lines that morph into an X ── */}
            <button
              type="button"
              onClick={() => { setSheetOpen(v => !v); setAvatarOpen(false); setNotifOpen(false); }}
              aria-expanded={sheetOpen}
              aria-controls="uf-nav-sheet"
              aria-label={sheetOpen ? 'Close menu' : 'Open menu'}
              className="relative ml-1 h-11 w-11 flex-none rounded-full bg-surface shadow-[0_0_0_1px_rgba(26,34,48,0.08),0_4px_12px_-6px_rgba(26,34,48,0.2)] lg:hidden"
            >
              <span aria-hidden="true" className={`absolute left-[14px] top-1/2 -mt-[0.75px] h-[1.5px] w-4 rounded-full bg-dark-primary transition-transform duration-500 ease-spring motion-reduce:transition-none ${sheetOpen ? 'rotate-45' : '-translate-y-[3.5px]'}`} />
              <span aria-hidden="true" className={`absolute left-[14px] top-1/2 -mt-[0.75px] h-[1.5px] w-4 rounded-full bg-dark-primary transition-transform duration-500 ease-spring motion-reduce:transition-none ${sheetOpen ? '-rotate-45' : 'translate-y-[3.5px]'}`} />
            </button>
          </div>
        </div>
      </header>

      {/* ── Row two (lg+): the site's sections ──
          Deliberately outside the sticky <header>: it sits under the bar at
          the top of a page and scrolls away beneath it like content, so the
          sticky part stays one 72px row (what DashboardDesk's top-[76px] and
          the measured inbox/applicants layouts assume). When it has gone,
          the same links dock inside the bar (renderSectionLinks('bar')). */}
      <div ref={rowTwoRef} className="hidden border-b border-line bg-surface lg:block">
        <nav aria-label="Main" className="mx-auto flex h-11 max-w-[1320px] items-center gap-7 px-5 sm:px-7">
          {renderSectionLinks('row')}
        </nav>
      </div>

      {/* ── Phone search (below md): its own row under the bar, scrolls away
          the same way. The sheet keeps its copy for once it has. Not on the
          homepage, whose hero already opens with the big search. Not on touch
          phones either: the Explore tab and each page's own search cover it,
          and the menu sheet still has it. ── */}
      {!isHomeRoute && (
        <div className="border-b border-line bg-surface px-3 pb-2.5 pt-1 md:hidden phone:hidden">
          <SiteSearch user={user} />
        </div>
      )}

      {/* ── Phone/tablet sheet ─────────────────────────────────────────────── */}
      <div
        id="uf-nav-sheet"
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        tabIndex={-1}
        className={`fixed inset-0 z-40 flex flex-col overflow-y-auto overscroll-contain bg-surface/[0.86] px-6 pb-[max(24px,env(safe-area-inset-bottom))] pt-[88px] font-sans outline-none backdrop-blur-2xl transition-[opacity,visibility] duration-500 ease-spring motion-reduce:transition-none lg:hidden ${
          sheetOpen ? 'visible opacity-100' : 'pointer-events-none invisible opacity-0'
        }`}
      >
        {sheetOpen && (
          <div className="mb-5 flex md:hidden">
            <SiteSearch user={user} />
          </div>
        )}
        <ul className="m-0 grid list-none gap-0.5 p-0">
          {sheetLinks.map((l, i) => (
            <li
              key={l.to}
              style={{ transitionDelay: sheetOpen ? `${90 + i * 55}ms` : '0ms' }}
              className={`transition-[opacity,transform] duration-700 ease-spring motion-reduce:transition-none ${sheetOpen ? 'translate-y-0 opacity-100' : 'translate-y-7 opacity-0'}`}
            >
              <Link
                to={l.to}
                aria-current={l.active ? 'page' : undefined}
                className="flex min-h-[52px] items-center justify-between gap-3 text-[28px] font-semibold tracking-[-0.025em] text-dark-primary no-underline"
              >
                {l.label}
                {l.badge > 0 ? (
                  <span className="inline-flex items-center gap-1.5 text-[12px] font-bold tracking-normal text-press-muted">
                    <BadgeTally value={l.badge} />{l.to === '/orders' ? 'need you' : ''}
                  </span>
                ) : l.active ? (
                  <span aria-hidden="true" className="h-[7px] w-[7px] rounded-full bg-dark-primary" />
                ) : null}
              </Link>
            </li>
          ))}
        </ul>

        <div
          style={{ transitionDelay: sheetOpen ? `${90 + sheetLinks.length * 55}ms` : '0ms' }}
          className={`mt-6 flex flex-wrap gap-x-5 gap-y-2 border-t border-line pt-5 transition-[opacity,transform] duration-700 ease-spring motion-reduce:transition-none ${sheetOpen ? 'translate-y-0 opacity-100' : 'translate-y-7 opacity-0'}`}
        >
          {sheetMore.map(l => (
            <Link key={l.to} to={l.to} className="py-1 text-[15px] font-medium text-ink-soft no-underline hover:text-dark-primary">{l.label}</Link>
          ))}
        </div>

        <div className="mt-auto grid gap-3 pt-8">
          {user ? (
            <>
              {isMember && !isInstructor && (
                <div className="flex rounded-full bg-dark-primary/[0.06] p-1" role="group" aria-label="Dashboard">
                  {[FREELANCING_MODE, HIRING_MODE].map(mode => {
                    const on = !onTeam && mode.key === activeMode.key;
                    return (
                      <button
                        key={mode.key}
                        type="button"
                        aria-pressed={on}
                        onClick={() => switchToMode(mode.key)}
                        className={`flex-1 rounded-full py-2.5 text-[14px] font-semibold transition-colors duration-300 ease-spring ${on ? 'bg-surface text-dark-primary shadow-[0_1px_2px_rgba(26,34,48,0.1)]' : 'text-ink-soft'}`}
                      >
                        {mode.key === 'freelancing' ? 'Selling' : 'Hiring'}
                      </button>
                    );
                  })}
                  {onTeam && (
                    <Link to={onTeam.to} onClick={() => setSheetOpen(false)} aria-current="page" className="flex-1 rounded-full bg-surface py-2.5 text-center text-[14px] font-semibold text-dark-primary no-underline shadow-[0_1px_2px_rgba(26,34,48,0.1)]">
                      Team
                    </Link>
                  )}
                </div>
              )}
              {isInstructor && (
                <div className="flex flex-wrap gap-2">
                  {dashboardRows.map(d => (
                    <Link key={d.key} to={d.to} className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[13px] font-semibold no-underline ${!onTeam && activeKey === d.key ? 'bg-dark-primary text-white' : 'bg-dark-primary/[0.06] text-dark-primary'}`}>
                      <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ background: DASHBOARD_COLORS[d.key] }} />
                      {DASHBOARD_SHORT[d.key]}
                    </Link>
                  ))}
                </div>
              )}
              <button type="button" onClick={signOut} className="justify-self-center py-2 text-[14px] font-medium text-ink-soft">
                Sign out
              </button>
            </>
          ) : (
            <div className="flex gap-2">
              <button type="button" onClick={() => { setSheetOpen(false); openAuthModal({ mode: 'signin' }); }} className="btn-island btn-island--ghost flex-1 justify-center pr-[22px]">
                Log in
              </button>
              <button type="button" onClick={() => { setSheetOpen(false); openAuthModal({ mode: 'signup' }); }} className="btn-island btn-island--navy flex-1 justify-between">
                Join
                <span className="btn-island-ic"><PiArrowUpRightLight size={17} aria-hidden="true" /></span>
              </button>
            </div>
          )}
        </div>
      </div>

      <PhoneTabBar
        isHiring={isHiring}
        workCount={user ? myWorkNeedsYou : 0}
        inboxCount={user ? inboxUnreadMessages : 0}
        active={isHomeRoute ? 'home'
          : location.pathname.startsWith('/inbox') ? 'inbox'
          : isAcademyActive ? 'academy'
          : (isMyWorkActive || location.pathname.includes('/dashboard')) ? 'work'
          : (isWorkActive || location.pathname.startsWith('/search')) ? 'explore'
          : null}
      />
    </>
  );
}

SiteHeader.propTypes = {
  user: PropTypes.object,
};

// ─────────────────────────────────────────────────────────────────────────────
// NotificationsPopover — the bell's panel. "All" and "Needs you" tabs, rows
// grouped by day, and the next step as a button right on any row that's
// waiting on you. Brass is only on those buttons (your move); unread is the
// 7px notify dot, never brass (DESIGN.md).
// ─────────────────────────────────────────────────────────────────────────────

function NotificationsPopover({ items, unreadCount, onMarkAllRead, onOpen, onClose }) {
  const [tab, setTab] = useState('all');
  const needsCount = items.filter(needsYou).length;
  const shown = tab === 'needs' ? items.filter(needsYou) : items;
  const groups = groupByDay(shown);

  return (
    <div className="fixed inset-x-2 top-[68px] z-[60] rounded-3xl bg-press-paper p-1.5 font-academy shadow-[0_0_0_1px_rgba(18,22,28,0.06)] animate-scale-in motion-reduce:animate-none sm:absolute sm:inset-x-auto sm:right-0 sm:top-[calc(100%+12px)] sm:w-[400px] sm:origin-top-right">
      <div className="overflow-hidden rounded-[18px] bg-press-card shadow-[0_1px_2px_rgba(18,22,28,0.05),0_24px_48px_-24px_rgba(18,22,28,0.35)]">
        <div className="flex items-center justify-between px-4 pt-3.5">
          <span className="font-press-display text-lg font-medium text-press-ink">Notifications</span>
          {unreadCount > 0 && (
            <button type="button" onClick={onMarkAllRead} className="rounded-md px-1 py-1 text-xs font-bold text-press-muted transition-colors hover:text-press-ink">
              Mark all read
            </button>
          )}
        </div>
        <div role="tablist" aria-label="Filter notifications" className="flex gap-5 border-b border-press-border px-4 pt-2">
          {[{ id: 'all', label: 'All' }, { id: 'needs', label: 'Needs you', count: needsCount }].map((t) => (
            <button
              key={t.id}
              id={`uf-notif-tab-${t.id}`}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              aria-controls="uf-notif-panel"
              onClick={() => setTab(t.id)}
              className={`-mb-px inline-flex items-baseline gap-1.5 border-b-2 pb-2.5 pt-1.5 text-[14px] font-semibold transition-colors ${
                tab === t.id ? 'border-press-ink text-press-ink' : 'border-transparent text-press-muted hover:text-press-ink'
              }`}
            >
              {t.label}
              {t.count > 0 && <span className="text-[12px] font-extrabold tabular-nums text-notify-deep">{t.count}</span>}
            </button>
          ))}
        </div>

        <div id="uf-notif-panel" role="tabpanel" aria-labelledby={`uf-notif-tab-${tab}`} className="max-h-[min(440px,calc(100dvh-200px))] overflow-y-auto px-1.5 pb-2">
          {shown.length === 0 ? (
            <p className="px-4 py-10 text-center text-[13px] text-press-muted">
              {tab === 'needs' ? 'Nothing is waiting on you.' : 'No notifications yet.'}
            </p>
          ) : groups.map((g) => (
            <div key={g.label}>
              <p className="px-2.5 pb-1 pt-3 text-xs font-bold text-press-muted">{g.label}</p>
              {g.items.map((n) => {
                // A warning is bad news, not routine activity — amber, not ink.
                const isWarning = n.category === 'warning';
                const action = needsYou(n) ? NEEDS_YOU_ACTION[n.type] : null;
                const href = n.type === 'onboarding-nudge' ? '#' : (n.link || '/notifications');
                return (
                  <div key={n._id} className="group relative flex gap-3 rounded-2xl p-2.5 transition-colors hover:bg-press-paper">
                    <span className={`mt-0.5 grid h-[34px] w-[34px] flex-none place-items-center rounded-full ${isWarning ? 'bg-warning/15 text-[#8A5A00]' : 'bg-press-paper-2 text-press-muted'}`}>
                      <NotifIcon category={n.category} />
                    </span>
                    <div className="min-w-0 flex-1">
                      {/* The whole row is the link (stretched over it); the
                          action button sits above it in the stacking order. */}
                      <a href={href} onClick={(e) => onOpen(n, e)} className="no-underline after:absolute after:inset-0 after:rounded-2xl after:content-[''] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-press-ink">
                        <p className={`m-0 text-[13px] leading-snug ${n.read ? 'font-medium text-press-muted' : 'font-bold text-press-ink'}`}>
                          {!n.read && <span className="sr-only">Unread: </span>}
                          {n.title}
                        </p>
                        {n.body && <p className="m-0 mt-0.5 line-clamp-2 text-[13px] leading-snug text-press-prose">{n.body}</p>}
                      </a>
                      {action && (
                        <a
                          href={href}
                          onClick={(e) => onOpen(n, e)}
                          className="relative z-[1] mt-2 inline-flex h-[30px] items-center rounded-full bg-press-ink px-3.5 text-[13px] font-bold text-press-paper no-underline transition-colors hover:bg-press-ink/90"
                        >
                          {action}
                        </a>
                      )}
                    </div>
                    <div className="flex flex-none flex-col items-end gap-1.5 pt-0.5">
                      <span className="text-[12px] tabular-nums text-press-muted">{shortTime(n.createdAt)}</span>
                      {!n.read && <span aria-hidden="true" className={`h-[7px] w-[7px] rounded-full ${isWarning ? 'bg-warning' : 'bg-notify'}`} />}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <Link
          to="/notifications"
          onClick={onClose}
          className="block border-t border-press-border py-2.5 text-center text-[13px] font-bold text-press-ink no-underline transition-colors hover:bg-press-paper"
        >
          View all
        </Link>
      </div>
    </div>
  );
}
NotificationsPopover.propTypes = {
  items: PropTypes.array.isRequired,
  unreadCount: PropTypes.number,
  onMarkAllRead: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

// StatChip — compact escrow/orders/contracts tile at the top of the avatar
// dropdown, so the menu answers "how am I doing" before any list item.
function StatChip({ value, label }) {
  return (
    <div className="flex-1 rounded-xl bg-press-paper px-2 py-1.5 text-center">
      <div className="text-[13px] font-bold tabular-nums text-dark-primary">{value}</div>
      <div className="text-[12px] text-ink-soft">{label}</div>
    </div>
  );
}
StatChip.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
};
